import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { getAvatarUrl } from './utils/Common';

const EmployeeManager = () => {
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Department form
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptDesc, setNewDeptDesc] = useState('');
  const [editingDept, setEditingDept] = useState(null);
  const [deptMessage, setDeptMessage] = useState({ type: '', text: '' });
  
  // Position form
  const [positionForm, setPositionForm] = useState({ department_id: '', name: '', description: '' });
  const [editingPosition, setEditingPosition] = useState(null);
  const [posMessage, setPosMessage] = useState({ type: '', text: '' });
  
  // Employee editing
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ department: '', position_id: null });
  const [availablePositions, setAvailablePositions] = useState([]);
  const [empMessage, setEmpMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Tab management
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'employees'; // 'employees' or 'management'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deptRes, posRes, empRes] = await Promise.all([
        api.get('/hr/departments'),
        api.get('/hr/positions'),
        api.get('/hr/employees')
      ]);
      setDepartments(deptRes.data);
      setPositions(posRes.data);
      setEmployees(empRes.data);
      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Department functions
  const handleAddDepartment = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    
    try {
      await api.post('/hr/departments', {
        name: newDeptName.trim(),
        description: newDeptDesc.trim() || null
      });
      setNewDeptName('');
      setNewDeptDesc('');
      setDeptMessage({ type: 'success', text: 'Department added successfully!' });
      loadData();
    } catch (err) {
      setDeptMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add department' });
    }
  };

  const handleUpdateDepartment = async () => {
    if (!editingDept) return;
    
    try {
      await api.put(`/hr/departments/${editingDept.id}`, {
        name: editingDept.name,
        description: editingDept.description
      });
      setEditingDept(null);
      setDeptMessage({ type: 'success', text: 'Department updated successfully!' });
      loadData();
    } catch (err) {
      setDeptMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update department' });
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Are you sure you want to delete "${dept.name}"?`)) return;
    
    try {
      await api.delete(`/hr/departments/${dept.id}`);
      setDeptMessage({ type: 'success', text: 'Department deleted successfully!' });
      loadData();
    } catch (err) {
      setDeptMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete department' });
    }
  };

  // Position functions
  const handleAddPosition = async (e) => {
    e.preventDefault();
    if (!positionForm.department_id || !positionForm.name.trim()) return;
    
    try {
      await api.post('/hr/positions', {
        name: positionForm.name.trim(),
        description: positionForm.description.trim() || null,
        department_id: parseInt(positionForm.department_id)
      });
      setPositionForm({ department_id: '', name: '', description: '' });
      setPosMessage({ type: 'success', text: 'Stanowisko dodane pomyślnie!' });
      loadData();
    } catch (err) {
      setPosMessage({ type: 'error', text: err.response?.data?.detail || 'Nie udało się dodać stanowiska' });
    }
  };

  const handleUpdatePosition = async () => {
    if (!editingPosition) return;
    
    try {
      await api.put(`/hr/positions/${editingPosition.id}`, {
        name: editingPosition.name,
        description: editingPosition.description
      });
      setEditingPosition(null);
      setPosMessage({ type: 'success', text: 'Stanowisko zaktualizowane pomyślnie!' });
      loadData();
    } catch (err) {
      setPosMessage({ type: 'error', text: err.response?.data?.detail || 'Nie udało się zaktualizować stanowiska' });
    }
  };

  const handleDeletePosition = async (position) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć stanowisko "${position.name}"?`)) return;
    
    try {
      await api.delete(`/hr/positions/${position.id}`);
      setPosMessage({ type: 'success', text: 'Stanowisko usunięte pomyślnie!' });
      loadData();
    } catch (err) {
      setPosMessage({ type: 'error', text: err.response?.data?.detail || 'Nie udało się usunąć stanowiska' });
    }
  };

  const getPositionsByDepartment = (deptId) => {
    return positions.filter(pos => pos.department_id === deptId);
  };

  const getDepartmentNameById = (deptId) => {
    return departments.find(d => d.id === deptId)?.name || 'Nieznany';
  };

  // Employee functions
  const startEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      department: emp.department || '',
      position_id: emp.position_id || null
    });
    
    // Load available positions for this employee's department
    if (emp.department) {
      const dept = departments.find(d => d.name === emp.department);
      if (dept) {
        setAvailablePositions(positions.filter(p => p.department_id === dept.id));
      }
    }
    setEmpMessage({ type: '', text: '' });
  };

  const handleEmployeeDepartmentChange = (departmentName) => {
    setEmployeeForm({ ...employeeForm, department: departmentName, position_id: null });
    
    // Load positions for the selected department
    const dept = departments.find(d => d.name === departmentName);
    if (dept) {
      setAvailablePositions(positions.filter(p => p.department_id === dept.id));
    } else {
      setAvailablePositions([]);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee) return;
    
    try {
      const updateData = {
        department: employeeForm.department || null,
        position_id: employeeForm.position_id || null
      };
      await api.put(`/hr/employees/${editingEmployee.id}`, updateData);
      setEditingEmployee(null);
      setEmpMessage({ type: 'success', text: 'Pomyślna aktualizacja!' });
      loadData();
    } catch (err) {
      setEmpMessage({ type: 'error', text: err.response?.data?.detail || 'Aktualizacja nie powiodła się!' });
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h2>👥 Zarządzanie pracownikami</h2>
          <p>Ładowanie...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h2>👥 Zarządzanie pracownikami</h2>
          <p className="error-text">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Zarządzanie jednostkami organizacyjnymi</h2>
        <p>Zarządzanie jednostkami organizacyjnymi i przypisywanie do nich pracowników</p>
      </div>

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'employees' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'employees'); return next; })}
          >
            👥 Lista pracowników
          </button>
          <button 
            className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
            onClick={() => setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('tab', 'management'); return next; })}
          >
            🏢 Zarządzanie strukturą
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'employees' ? (
          // TAB 1: Employee List
          <div className="tab-content">
            <div className="manager-section employees-section">
              <h3>Lista pracowników</h3>
              
              {empMessage.text && (
                <div className={`message ${empMessage.type}`}>
                  {empMessage.text}
                </div>
              )}

              {/* Search Input */}
              <div className="search-container employee-search">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Wyszukaj pracownika (imię, nazwisko)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button 
                    className="search-clear"
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="employees-table-container">
                <table className="employees-table">
                  <thead>
                    <tr>
                      <th>Pracownik</th>
                      <th>Jednostka</th>
                      <th>Pozycja</th>
                      <th>Akcja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees
                      .filter(emp => 
                        emp.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map(emp => (
                      <tr key={emp.id}>
                        <td>
                          <div className="emp-info">
                            <div className="emp-avatar-small">
                              {getAvatarUrl(emp.avatar) ? (
                                <img src={getAvatarUrl(emp.avatar)} alt={emp.full_name} />
                              ) : (
                                <span>{emp.full_name?.charAt(0).toUpperCase() || '?'}</span>
                              )}
                            </div>
                            <div>
                              <strong>{emp.full_name}</strong>
                              <span className="emp-email">{emp.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{emp.department || '-'}</td>
                        <td>{emp.position_name || '-'}</td>
                        <td>
                          <button 
                            onClick={() => startEditEmployee(emp)} 
                            className="btn-secondary btn-sm"
                          >
                            Edytuj
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          // TAB 2: Department & Position Management
          <div className="tab-content">
            <div className="employee-manager-grid">
              {/* Departments Section */}
              <div className="manager-section">
                <h3>Jednostki organizacyjne</h3>
                
                {deptMessage.text && (
                  <div className={`message ${deptMessage.type}`}>
                    {deptMessage.text}
                  </div>
                )}

                {/* Add Department Form */}
                <form onSubmit={handleAddDepartment} className="add-dept-form">
                  <input
                    type="text"
                    placeholder="Nazwa jednostki"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    className="form-input"
                  />
                  <input
                    type="text"
                    placeholder="Opis (opcjonalnie)"
                    value={newDeptDesc}
                    onChange={(e) => setNewDeptDesc(e.target.value)}
                    className="form-input"
                  />
                  <button type="submit" className="btn-primary">
                    ➕ Dodaj jednostkę
                  </button>
                </form>

                {/* Department List */}
                <div className="dept-list">
                  {departments.length === 0 ? (
                    <p className="no-data">Brak jednostek organizacyjnych. Dodaj jedną powyżej.</p>
                  ) : (
                    departments.map(dept => (
                      <div key={dept.id} className="dept-item">
                        {editingDept?.id === dept.id ? (
                          <div className="dept-edit-form">
                            <input
                              type="text"
                              value={editingDept.name}
                              onChange={(e) => setEditingDept({ ...editingDept, name: e.target.value })}
                              className="form-input"
                            />
                            <input
                              type="text"
                              value={editingDept.description || ''}
                              onChange={(e) => setEditingDept({ ...editingDept, description: e.target.value })}
                              placeholder="Opis (opcjonalnie)"
                              className="form-input"
                            />
                            <div className="dept-edit-actions">
                              <button onClick={handleUpdateDepartment} className="btn-success btn-sm">
                                ✓ Zapisz
                              </button>
                              <button onClick={() => setEditingDept(null)} className="btn-secondary btn-sm">
                                ✕ Anuluj
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="dept-info">
                              <strong>{dept.name}</strong>
                              {dept.description && <span className="dept-desc">{dept.description}</span>}
                              <span className="dept-count">
                                {employees.filter(e => e.department === dept.name).length} pracowników
                              </span>
                            </div>
                            <div className="dept-actions">
                              <button 
                                onClick={() => setEditingDept({ ...dept })} 
                                className="btn-icon"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button 
                                onClick={() => handleDeleteDepartment(dept)} 
                                className="btn-icon btn-icon-danger"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Positions Section */}
              <div className="manager-section">
                <h3>Stanowiska</h3>
                
                {posMessage.text && (
                  <div className={`message ${posMessage.type}`}>
                    {posMessage.text}
                  </div>
                )}

                {/* Add Position Form */}
                <form onSubmit={handleAddPosition} className="add-dept-form">
                  <select
                    value={positionForm.department_id}
                    onChange={(e) => setPositionForm({ ...positionForm, department_id: e.target.value })}
                    className="form-input form-select"
                  >
                    <option value="">Wybierz jednostkę</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Nazwa stanowiska"
                    value={positionForm.name}
                    onChange={(e) => setPositionForm({ ...positionForm, name: e.target.value })}
                    className="form-input"
                  />
                  <input
                    type="text"
                    placeholder="Opis (opcjonalnie)"
                    value={positionForm.description}
                    onChange={(e) => setPositionForm({ ...positionForm, description: e.target.value })}
                    className="form-input"
                  />
                  <button type="submit" className="btn-primary">
                    ➕ Dodaj stanowisko
                  </button>
                </form>

                {/* Position List */}
                <div className="dept-list">
                  {positions.length === 0 ? (
                    <p className="no-data">Brak stanowisk. Dodaj jedno powyżej.</p>
                  ) : (
                    departments.map(dept => {
                      const deptPositions = getPositionsByDepartment(dept.id);
                      if (deptPositions.length === 0) return null;
                      
                      return (
                        <div key={`dept-${dept.id}`} className="position-group">
                          <h4 className="position-group-header">{dept.name}</h4>
                          {deptPositions.map(pos => (
                            <div key={pos.id} className="dept-item">
                              {editingPosition?.id === pos.id ? (
                                <div className="dept-edit-form">
                                  <input
                                    type="text"
                                    value={editingPosition.name}
                                    onChange={(e) => setEditingPosition({ ...editingPosition, name: e.target.value })}
                                    className="form-input"
                                  />
                                  <input
                                    type="text"
                                    value={editingPosition.description || ''}
                                    onChange={(e) => setEditingPosition({ ...editingPosition, description: e.target.value })}
                                    placeholder="Opis (opcjonalnie)"
                                    className="form-input"
                                  />
                                  <div className="dept-edit-actions">
                                    <button onClick={handleUpdatePosition} className="btn-success btn-sm">
                                      ✓ Zapisz
                                    </button>
                                    <button onClick={() => setEditingPosition(null)} className="btn-secondary btn-sm">
                                      ✕ Anuluj
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="dept-info">
                                    <strong>{pos.name}</strong>
                                    {pos.description && <span className="dept-desc">{pos.description}</span>}
                                    <span className="dept-count">
                                      {employees.filter(e => e.position_id === pos.id).length} pracowników
                                    </span>
                                  </div>
                                  <div className="dept-actions">
                                    <button 
                                      onClick={() => setEditingPosition({ ...pos })} 
                                      className="btn-icon"
                                      title="Edytuj"
                                    >
                                      ✏️
                                    </button>
                                    <button 
                                      onClick={() => handleDeletePosition(pos)} 
                                      className="btn-icon btn-icon-danger"
                                      title="Usuń"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Employee Modal */}
        {editingEmployee && (
          <div className="modal-overlay" onClick={() => setEditingEmployee(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edycja pracownika: {editingEmployee.full_name}</h3>
                <button className="modal-close" onClick={() => setEditingEmployee(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Jednostka</label>
                  <select
                    value={employeeForm.department}
                    onChange={(e) => handleEmployeeDepartmentChange(e.target.value)}
                    className="form-input form-select"
                  >
                    <option value="">Wybierz jednostkę</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Pozycja</label>
                  <select
                    value={employeeForm.position_id || ''}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, position_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="form-input form-select"
                    disabled={!employeeForm.department}
                  >
                    <option value="">Wybierz pozycję</option>
                    {availablePositions.map(pos => (
                      <option key={pos.id} value={pos.id}>{pos.name}</option>
                    ))}
                  </select>
                  {!employeeForm.department && (
                    <small className="form-help">Najpierw wybierz jednostkę</small>
                  )}
                </div>

              </div>
              <div className="modal-footer">
                <button onClick={() => setEditingEmployee(null)} className="btn-danger">
                  Anuluj
                </button>
                <button onClick={handleUpdateEmployee} className="btn-success">
                  Zapisz zmiany
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeManager;
